import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { ChartTwoIcon, PlusIcon, PrintIcon, UploadIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { getTotalAllocatedBudgets } from '../../api/buget-allocation/get-total-allocated-budgets'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import BudgetAllocationDeleteButton from '../../components/pages/budget-allocations/budget-allocation-delelte-button'
import BudgetAllocationGenerateReceiptButton from '../../components/pages/budget-allocations/budget-allocation-generate-receipt-button'
import BudgetAllocationFilter from '../../components/pages/budget-allocations/budget-allocations-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { BudgetLine } from '../../lib/types/models/budget-line/budget-line'
import { BudgetAllocationFilters } from '../../lib/types/models/budget-line/budget-line-filter'
import {
  transformBudgetAllocationFiltersToUrl,
  transformUrlToBudgetAllocationFilters
} from '../../lib/utils/filters-mapping/budget-allocation'
import { queryClient } from '../../main'
import { constructCenterPath } from '../centers/utils'

export const budgetAllocationInitialFilters: BudgetAllocationFilters = {}

const BudgetAllocationListPage = () => {
  const { navigate } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<BudgetAllocationFilters>({
      initialFilters: budgetAllocationInitialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformBudgetAllocationFiltersToUrl,
      transformUrlToFilter: transformUrlToBudgetAllocationFilters
    })

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])
  const tableRef = React.useRef<DynamicTableRef<BudgetLine>>(null)

  const { data: totalAllocatedBudgets, isLoading: gettingTotalAllocatedBudgets } = useQuery({
    queryKey: ['totalAllocatedBudgets'],
    queryFn: () => getTotalAllocatedBudgets(),
    select: (data) => data.map((item) => NumberFormatter.formatCurrency(item.price, item.currency))
  })

  const columns: Column<BudgetLine>[] = [
    {
      accessorKey: 'center',
      enableSorting: true,
      header: 'Centre',
      cell: ({ row }) => (
        <Typography.Link
          variant="secondary-body"
          onClick={() =>
            navigate(constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, row.original.center.id))
          }
        >
          {row.original.center.name}
        </Typography.Link>
      )
    },
    {
      accessorKey: 'centerManager',
      header: 'Responsable centre',
      cell: ({ row }) => {
        const centerManager = row.original.centerManager
        if (!centerManager || !centerManager.staff) return '-'

        return (
          <Typography.Link
            variant="secondary-body"
            onClick={() =>
              navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', centerManager.staff.id.toString()))
            }
          >
            {StringFormatter.capitalize(centerManager.staff.firstName)}{' '}
            {centerManager.staff.lastName.toUpperCase()}
          </Typography.Link>
        )
      }
    },
    {
      accessorKey: 'rad',
      header: 'RAD',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.center.rad?.title ? (
          <Typography.Link
            variant="secondary-body"
            onClick={() =>
              navigate(
                Paths.RAD_UPDATE_PAGE.replace(':id', row.original.center.rad?.id?.toString() || '')
              )
            }
          >
            {row.original.center.rad?.title}
          </Typography.Link>
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'price',
      header: 'Montant',
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant="secondary-body">
          {NumberFormatter.formatCurrency(row.original.price, row.original.currency)}
        </Typography>
      )
    },
    {
      accessorKey: 'printedAt',
      header: "Date d'impression",
      enableSorting: true,
      cell: ({ row }) =>
        row.original.printedAt ? (
          <Badge variant="grey" text={DateFormatter.DDMMYYYY(row.original.printedAt)} />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-4 justify-end pr-4">
          <BudgetAllocationGenerateReceiptButton
            id={row.original.id}
            onSuccess={() => {
              tableRef.current?.refetch()
            }}
          />
          <BudgetAllocationDeleteButton
            id={row.original.id}
            onDelete={() => {
              tableRef.current?.refetch()
              queryClient.invalidateQueries({ queryKey: ['totalAllocatedBudgets'] })
              toast.success(`Allocation budgétaire supprimée avec succès`)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Allocation Budgétaire" icon={ChartTwoIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<BudgetLine, BudgetAllocationFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Liste des superviseurs participants"
        columns={columns}
        allowSearchParam
        endpoint={API.budgetAllocation.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        version={'LIGHT'}
        filtersLength={countFilters(selectedFilter)}
        selectable
        label={() => (
          <Badge
            loading={gettingTotalAllocatedBudgets}
            text={`Total : ${Array.isArray(totalAllocatedBudgets) && totalAllocatedBudgets.length > 0 ? totalAllocatedBudgets?.join(' | ') : 0}`}
            variant="warning"
            size="medium"
          />
        )}
        permissions={{
          delete: false
        }}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter une ligne budgétaire',
            fn: () => navigate(Paths.BUDGET_ALLOCATION_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: PrintIcon,
            variant: 'ghost',
            label: 'Imprimer les reçus',
            fn: async (rows) => {
              await downloadDocuments({
                ids: rows.map(({ id }) => id),
                endpoint: API.budgetAllocation.generateBudgetAllocationReceipts.url
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            variant: 'ghost',
            label: 'Exporter en CSV',
            fn: async (rows) => {
              const ids = rows.map((row) => row.id)
              await downloadDocuments({ ids, endpoint: API.budgetAllocation.exportExcel.url })
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <BudgetAllocationFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default BudgetAllocationListPage
